import { FC, useState, useContext, useMemo } from "react";
import { QESAlert, QESCheckbox, QESSelect, QESTextInput, QESButton } from "../shared/qes-standards";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { DocumentTypes } from "../admin/specialty-group-exception/documentTypesEnum";
import { toast } from "react-toastify";
import { ExportCmsExceptionReportRequestModel } from "../exports/exportModels";
import { ExportFormatType } from "@app/exports/enumerations";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import ExceptionsService from "./exceptionsService";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

interface GenerateDocumentFlyoutProps {
  selectedExceptionIds: number[];
  resetSelectedCheckboxes: () => void;
  customProjectName: string[];
  enableRegulatoryExceptionReports: boolean[];
  haveRemovedServiceArea: boolean[];
  haveRemovedSpecialty: boolean[];
  refreshData: () => void;
}

const GenerateDocumentFlyout: FC<GenerateDocumentFlyoutProps> = (props: GenerateDocumentFlyoutProps) => {
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentTypes>(DocumentTypes.CmsMa);
  const [zipFileName, setZipFileName] = useState<string>(helpers.reportName("CMS_Exception_Report"));
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const exceptionsService = new ExceptionsService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const flyoutContext = useContext(FlyoutContext);

  const handleGenerateDocument = async () => {
    if (!selectedDocumentType || !zipFileName || props.selectedExceptionIds.length === 0) return;

    const payload: ExportCmsExceptionReportRequestModel = {
      documentType: selectedDocumentType,
      eMailOnSuccess: emailOnSuccess,
      exportName: zipFileName,
      formatType: ExportFormatType.CompressedBinary,
      runAsynchronous: true,
      specialtyGroupExceptionIds: props.selectedExceptionIds,
    };

    try {
      await exceptionsService.exportCmsExceptionReport(payload);
      toast.info(`${zipFileName} has been queued for generation. Please check email / Exception Reports page for status.`, {
        containerId: "z-index-1030",
      });
      props.resetSelectedCheckboxes();
      props.refreshData();
      flyoutContext.handleCloseFlyout();
    } catch (error) {
      toast.error("Error generating document");
    } finally {
      resetFlyoutState();
    }
  };

  const resetFlyoutState = () => {
    setSelectedDocumentType(null);
    setZipFileName(helpers.reportName("CMS_Exception_Report"));
    setEmailOnSuccess(true);
  };

  const alertMessage = featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS)
    ? (<>Document will be downloaded as a .zip file.<br />Any supplemental documentation will be included in the export file.</>)
    : (<>Document will be downloaded as a .zip file.</>);

  const checkConditions = (conditions: boolean[]) => {
    const allTrue = !conditions.some(value => !value);
    const mixed = conditions.some(value => !value) && conditions.some(value => value);
    return { allTrue, mixed };
  };

  const getProjects = (conditions: boolean[], projectNames: string[]) => {
    return Array.from(new Set(projectNames.filter((_, index) => conditions[index])))
      .sort((a, b) => a.localeCompare(b));
  };

  const { allTrue: insufficientPrivileges, mixed: mixedPrivileges } = checkConditions(props.enableRegulatoryExceptionReports.map(value => !value));
  const projectsWithInsufficientPrivileges = useMemo(() => getProjects(props.enableRegulatoryExceptionReports.map(value => !value), props.customProjectName), [props.customProjectName, props.enableRegulatoryExceptionReports]);

  const { allTrue: allHaveRemovedServiceArea, mixed: mixedHaveRemovedServiceArea } = checkConditions(props.haveRemovedServiceArea);
  const projectsWithRemovedServiceArea = useMemo(() => getProjects(props.haveRemovedServiceArea, props.customProjectName), [props.customProjectName, props.haveRemovedServiceArea]);

  const { allTrue: allHaveRemovedSpecialty, mixed: mixedHaveRemovedSpecialty } = checkConditions(props.haveRemovedSpecialty);
  const projectsWithRemovedSpecialty = useMemo(() => getProjects(props.haveRemovedSpecialty, props.customProjectName), [props.customProjectName, props.haveRemovedSpecialty]);

  //check to see if any selected items meets criteria to generate document:
  //does not have removed service area && does not have removed specialty && has priveleges,
  const submitCriteriaCheckPassed = useMemo(() => {
    return props.customProjectName.some((_, index) => !props.haveRemovedServiceArea[index] && !props.haveRemovedSpecialty[index] && props.enableRegulatoryExceptionReports[index]);
  }, [props.customProjectName, props.haveRemovedSpecialty, props.haveRemovedServiceArea, props.enableRegulatoryExceptionReports]);

  return (
    <>
      <div>
        <div className="row">
          <div className="col-10 mb-3">
            {mixedPrivileges && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the CMS Exception Template. Insufficient privileges for the following projects. Please contact Quest Client Services for assistance.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithInsufficientPrivileges.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {insufficientPrivileges && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the CMS Exception Template. Insufficient privileges for all selected projects. Please contact Quest Client Services for assistance.</strong>
              </QESAlert>
            )}
            {mixedHaveRemovedServiceArea && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the CMS Exception Template. The following selected exceptions no longer have the service area in the project. Please check the service area in the project and try again.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithRemovedServiceArea.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {allHaveRemovedServiceArea && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the CMS Exception Template. All selected exceptions no longer have the service area in the project. Please check the service area in the project and try again.</strong>
              </QESAlert>
            )}
            {mixedHaveRemovedSpecialty && (
              <QESAlert
                type="warning"
                persist={true}
                collapseTitle={
                  <>
                    <strong>Unable to generate the CMS Exception Template. The following selected exceptions no longer have the specialty listed in the project. Please check your selected exception and try again.</strong>
                  </>
                }
              >
                <ul>
                  {projectsWithRemovedSpecialty.map((project, index) => (
                    <li key={index}>{project}</li>
                  ))}
                </ul>
              </QESAlert>
            )}
            {allHaveRemovedSpecialty && (
              <QESAlert classNames="mb-3" type="danger" persist={true}>
                <strong>Unable to generate the CMS Exception Template. All selected exceptions no longer have the specialty listed in the project. Please check your selected exception and try again.</strong>
              </QESAlert>
            )}
            <QESAlert classNames="mb-3" type="info" persist={true}>
              <strong>{alertMessage}</strong>
            </QESAlert>
            <QESSelect
              value={selectedDocumentType || ""}
              onChange={(e) => setSelectedDocumentType(e.target.value as DocumentTypes)}
              placeholder="Select Document Type"
              label="Document Type"
              testId="exception__document-type-select"
              required={true}
              isValid={!!selectedDocumentType}
              allowEmpty={false}
            >
              {[DocumentTypes.CmsMa, DocumentTypes.CmsMmp].map(type => (
                <option key={type} value={type}>
                  {type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()}
                </option>
              ))}
            </QESSelect>
            <br />
            <QESTextInput
              value={zipFileName}
              onChange={(e) => setZipFileName(e.target.value)}
              placeholder="Enter Zip File Name"
              label="Zip File Name"
              testId="exception__zip-file-name-input"
              required={true}
              isValid={!!zipFileName}
            />
            <br />
            <QESCheckbox
              label="Alert me when the report is ready to view"
              checked={emailOnSuccess}
              onChange={(e) => setEmailOnSuccess((e.target as HTMLInputElement).checked)}
              id="exception__download-all-flyout__email-me"
              testId="exception__download-all-flyout__email-me"
            />
          </div>
        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="generate-document-flyout__save"
            disabled={!selectedDocumentType || !zipFileName || !submitCriteriaCheckPassed}
            onClick={handleGenerateDocument}
          >
            Confirm
          </QESButton>
        }
      />
    </>
  );
};

export default GenerateDocumentFlyout;
